// src/components/organisms/BlogSection/BlogSection.tsx

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchArticles } from '../../../services/articleService'
import type { Article } from '../../../types/article'
import styles from './BlogSection.module.css'

/* ─────────────────────────────────────────────────
   Icône SVG inline
───────────────────────────────────────────────── */

const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={styles.arrowIcon}>
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
)

/* ─────────────────────────────────────────────────
   Mini card article
───────────────────────────────────────────────── */

function BlogCard({ article }: { article: Article }) {
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric',
    })

  return (
    <Link
      to={`/blog/${article.slug}`}
      className={styles.card}
      aria-label={`Lire l'article : ${article.title}`}
    >
      {article.cover && (
        <div className={styles.cardCover}>
          <img src={article.cover} alt="" aria-hidden="true" className={styles.cardCoverImg} loading="lazy" />
        </div>
      )}
      <div className={styles.cardBody}>
        <span className={styles.cardCategory}>{article.categories?.[0] || 'Général'}</span>
        <h3 className={styles.cardTitle}>{article.title}</h3>
        <p className={styles.cardExcerpt}>{article.description}</p>
        <div className={styles.cardFooter}>
          <time dateTime={article.publishedAt} className={styles.cardDate}>
            {formatDate(article.publishedAt)}
          </time>
          <span className={styles.cardReadMore}>
            Lire <IconArrowRight />
          </span>
        </div>
      </div>
    </Link>
  )
}

/* ─────────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────────── */

export function BlogSection() {
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading]   = useState(true)

  useEffect(() => {
    fetchArticles()
      .then((data) => {
        const latest = [...data]
          .filter((a) => a.isPublished)
          .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
          .slice(0, 3)
        setArticles(latest)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <section className={styles.section} aria-label="Derniers articles">
        <div className={styles.inner}>
          <h2 className={styles.sectionTitle}>Blog</h2>
          <div className={styles.titleBar} aria-hidden="true" />
          <div className={styles.grid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.skeletonCard} aria-hidden="true">
                <div className={`${styles.sk} ${styles.skCover}`} />
                <div className={styles.skBody}>
                  <div className={`${styles.sk} ${styles.skTag}`} />
                  <div className={`${styles.sk} ${styles.skTitle}`} />
                  <div className={`${styles.sk} ${styles.skLine}`} />
                  <div className={`${styles.sk} ${styles.skLineShort}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (articles.length === 0) return null

  return (
    <section className={styles.section} id="blog" aria-label="Derniers articles">
      <div className={styles.bgGrid} aria-hidden="true" />

      <div className={styles.inner}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Blog</h2>
            <div className={styles.titleBar} aria-hidden="true" />
            <p className={styles.sectionSubtitle}>
              Venez découvrir mes derniers articles sur différents sujets tech, non-tech, ou simplement mes réflexions du moment.
            </p>
          </div>
        </div>

        <div className={styles.grid}>
          {articles.map((article) => (
            <BlogCard key={article.id} article={article} />
          ))}
        </div>

        <div className={styles.cta}>
          <Link to="/blog" className={styles.ctaBtn}>
            Voir tous les articles <IconArrowRight />
          </Link>
        </div>
      </div>
    </section>
  )
}
